import { useContext, type FunctionComponent } from 'react';
import { FEATURE_FLAGS } from '../../../constants/feature-flags';
import { MEDALLIA_PDP_SPECS_EMBEDDED_FORM_ID, MEDALLIA_PDP_SPECS_FORM_ID } from '../../../constants/medallia';
import { VariantContext } from '../../../contexts/variant/variant.context';
import { getKeySpecAttributes } from '../../../helpers/product-helper/product.helper';
import { useFeature } from '../../../hooks/features/features.hooks';
import { useMedalliaEmbeddedForm } from '../../../hooks/medallia/medallia.hooks';
import { type ProductAttribute, type PdpProductFamily } from '../../../types/product.types';
import { TextButton } from '../../buttons/text-button/text-button.component';
import { HelpIcon } from '../../common-components/help-icon/help-icon.component';
import { CloudinaryImage } from '../../images/cloudinary-image/cloudinary-image.component';
import { ManufacturerResources } from '../manufacturer-resource/manufacturer-resource.component';
import { ProductAttributes } from '../product-attributes/product-attributes.component';

export type ProductSpecsProps = {
	product: PdpProductFamily;
};

const KeySpecs: FunctionComponent<{ specs: ProductAttribute[] }> = ({ specs }) => (
	<div>
		<h3 className="text-xl mt-0">Key Specifications</h3>
		<ul className="flex flex-col items-start m-0 text-sm list-none p-0">
			{specs.map(({ id, name, values, units, description }) => (
				<li key={`${id}-${name}`} className="mb-2">
					<span className="font-bold">{name}</span>
					<span className="ml-2 align-middle">
						<HelpIcon>
							<div className="text-sm">{description}</div>
						</HelpIcon>
					</span>
					<span className="ml-2">
						{values} {units}
					</span>
				</li>
			))}
		</ul>
	</div>
);

const SpecsFeedback: FunctionComponent = () => {
	const { isFormRequested, isSDKReady, requestForm } = useMedalliaEmbeddedForm(MEDALLIA_PDP_SPECS_FORM_ID);

	if (!isSDKReady) {
		return null;
	}

	return (
		<div className="flex flex-col items-center mt-4">
			{!isFormRequested && (
				<TextButton
					className="my-2 text-sm! whitespace-pre-line"
					color="grey-darker"
					underline
					onClick={requestForm}
					testId="specs-feedback-button">
					{'Did you find what you are looking for?\nLeave us feedback on how we can improve.'}
				</TextButton>
			)}
			{isFormRequested && (
				<div
					className="mt-2 w-full max-w-150 bg-secondary p-4 rounded"
					id={MEDALLIA_PDP_SPECS_EMBEDDED_FORM_ID}
					data-testid="specs-feedback-form"
				/>
			)}
		</div>
	);
};

export const ProductSpecs: FunctionComponent<ProductSpecsProps> = ({ product }) => {
	const { selectedVariant } = useContext(VariantContext);
	const medalliaFormActive = useFeature(FEATURE_FLAGS.PDP_SPECS_MEDALLIA_FORM, { isSiteWide: true });
	const { id, description, imageType } = selectedVariant.image;
	const displayableKeySpecs = getKeySpecAttributes(product.attributeGroups ?? []);
	const hasKeySpecs = displayableKeySpecs.length > 0;
	const hasResources = product.resources && product.resources.length > 0;
	return (
		<section className="p-4">
			{(hasKeySpecs || hasResources) && (
				<div className="flex flex-col md:flex-row pb-4 border-b b--theme-grey-lighter">
					{hasKeySpecs && (
						<div className="flex w-full md:w-1/2">
							<CloudinaryImage
								publicID={id}
								options={{ width: 175, height: 175, type: imageType }}
								description={description}
								className="w-auto self-center hidden lg:block mr-4"
								bypassLazyLoad={true}
							/>
							<KeySpecs specs={displayableKeySpecs} />
						</div>
					)}
					{hasResources && (
						<div className="w-full md:w-1/2">
							<ManufacturerResources resources={product.resources} titleClassName="md:mt-0" />
						</div>
					)}
				</div>
			)}
			{product.attributeGroups?.map((attributeGroup) => (
				<div className="page-avoid-p" key={attributeGroup.name}>
					<ProductAttributes headingValue="h3" attributeGroup={attributeGroup} />
				</div>
			))}
			{medalliaFormActive && <SpecsFeedback />}
		</section>
	);
};
